import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, FormControl, Grid, MenuItem, OutlinedInput, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import { AUTHORITIES } from 'app/config/constants';
import { useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { ISearchTypes } from 'app/shared/interfaces/searchTypesInterface';
import { ICompany } from 'app/shared/model/company.model';
import { PeriodicType } from 'app/shared/model/enumerations/periodicType';
import { StatusFilter } from 'app/shared/model/enumerations/status-filter.model';
import { TireAlert } from 'app/shared/model/enumerations/tire-alert.model';
import { showElement } from 'app/shared/util/data-utils';
import { ASC } from 'app/shared/util/pagination.constants';
import React, { Fragment, useEffect, useState } from 'react';
import { IPaginationBaseState, translate } from 'react-jhipster';
import { NewCustomSearchInput } from '../custom-search-input/NewCustomSearchInput';
import { DateInput } from '../date-input/DateInput';
import { EnumSelectAlertCluster } from '../enum-select-alert-cluster/EnumSelectAlertCluster';
import EnumSelectTireAlert from '../enum-select-tire-alert/EnumSelectTireAlert';
import { PeriodButtonsNew } from '../period-buttons/PeriodButtonsNew';
import { SelectDefault } from '../select-default/SelectDefault';
import SelectPaginate from '../select-paginate/SelectPaginate';
import SelectPaginateFromPaginatedApi from '../select-paginate/SelectPaginateFromPaginatedApi';
import StatusFilterSelect from '../status-filter-select/StatusFilterSelect';
import ValidatedFormsNew from '../validated-form/ValidatedFormsNew';

interface PageFiltersProps {
  sortQuery?: string;
  setActivePage?: (page?: number) => void;
  isAdmin?: boolean;
  isManufacturerAdmin?: boolean;
  searchTypes?: ISearchTypes[];
  status?: StatusFilter | string;
  setStatus?: (status: StatusFilter) => void;
  setTireAlertSearch?: (alert: TireAlert) => void;
  typeTireAlert?: string;
  searchCompanyName?: string;
  handleSearchCompany?: (company: ICompany) => void;
  companySelectApiUrl?: string;
  companySelectClearable?: boolean;
  getAllEntities?: () => void;
  searchLicensePlate?: string;
  handleSearchLicensePlate?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  licensePlateSelectValue?: any;
  handleSearchLicensePlateSelect?: (value: any) => void;
  licensePlateSelectApiUrl?: string;
  licensePlateSelectDisabled?: boolean;
  licensePlateSelectClearable?: boolean;
  eventSelectValue?: any;
  eventSelectOptions?: any[];
  handleEventSelect?: (value: any) => void;
  eventSelectLabel?: string;
  eventSelectPlaceholder?: string;
  searchRepresentative?: string;
  handleSearchRepresentative?: (company: ICompany) => void;
  loading?: boolean;
  paginationState?: IPaginationBaseState;
  sort?: () => void;
  sortByName?: (sort: string) => void;
  sortClear?: () => void;
  sendSensorDataByImplement?: any;
  dateStart?: string | null;
  setDateStart?: (date: string | null) => void;
  dateEnd?: string | null;
  setDateEnd?: (date: string | null) => void;
  isOpen?: boolean;
  toggle?: () => void;
  sendSensorDataByImplementIdAndPeriodic?: any;
  changeToCustomPeriodic?: any;
  isCompany?: boolean;
  setLoading?: boolean;
  periodicType?: PeriodicType;
  activeButton?: any;
  handleActiveButton?: any;
  ActiveButton?: any;
  enabledAlertTypes?: any[];
  setSensorName?: string;
  dateSearch?: string;
  handleDateSearch?: (date: string) => void;
  searchCluster?: string;
  setSearchCluster?: (cluster: string) => void;
  showFilters?: Array<
    | 'company'
    | 'status'
    | 'licensePlate'
    | 'licensePlateSelect'
    | 'eventSelect'
    | 'searchButton'
    | 'filterOrder'
    | 'clearFilters'
    | 'periodic'
    | 'alert'
    | 'filters'
    | 'manufacturer'
    | 'tireAlert'
    | 'date'
    | 'alertCluster'
  >;
  isPeriodAbsolute?: boolean;
  onlyPastData?: boolean;
  noTime?: boolean;
  isClassification?: boolean;
  showMonthlyPeriods?: boolean;
  monthOnly?: boolean;
  alertTypeSelect?: boolean;
  periodSelect?: boolean;
  submitLabel?: string;
  appliedPeriodLabel?: string;
  maxRangeDays?: number;
  maxRangeDisplayDays?: number;
  onlyTransporters?: boolean;
  childrenAfterLicensePlate?: boolean;
  compactClearFilters?: boolean;
  searchButtonDisabled?: boolean;
  filterWidthSm?: number | string;
  filterWidthMd?: number | string;
  compact?: boolean;
  children?: React.ReactNode;
  // A filter rendered as children is active, so "Limpar Filtros" must show.
  hasExtraFilters?: boolean;
}

const CompanyFilter = ({
  handleSearchCompany,
  searchCompanyName,
  searchRepresentative,
  getAllEntities,
  sortQuery,
  companySelectApiUrl,
  companySelectClearable = false,
  onlyTransporters = false,
  loading = false,
  filterWidthSm,
  filterWidthMd,
}) => {
  const theme = useTheme();
  const isManufacturerAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.MANUFACTURER_ADMIN]));

  const [selectKey, setSelectKey] = useState(Date.now());

  useEffect(() => {
    setSelectKey(Date.now());
  }, [searchRepresentative]);

  // Determine the API URL based on context
  const getApiUrl = () => {
    if (companySelectApiUrl) {
      return companySelectApiUrl;
    }
    if (onlyTransporters) {
      return 'api/companies/transporter';
    }
    return isManufacturerAdmin ? 'api/companies/manufacturer' : 'api/companies';
  };

  return (
    <Grid
      item
      sx={{
        minWidth: 0,
        width: filterWidthMd || 300,
        [theme.breakpoints.down('sm')]: {
          width: '100%',
        },
      }}
    >
      <SelectPaginate
        key={selectKey}
        apiUrl={getApiUrl()}
        value={searchCompanyName ? { id: 0, name: searchCompanyName } : null}
        searchRepresentative={searchRepresentative}
        placeholder="Filtrar por empresa"
        onChange={handleSearchCompany}
        valueName="name"
        label="Empresa"
        isRequired
        isClearable={companySelectClearable}
        statusFilter
        sortQuery={sortQuery}
        debounceTimeout={500}
        isDisabled={loading}
        minWidth={filterWidthSm}
        desktopMinWidth={filterWidthMd}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            event.preventDefault();
            getAllEntities();
          }
        }}
      />
    </Grid>
  );
};

const ManufacturerFilter = ({ handleSearchRepresentative, searchRepresentative, getAllEntities, sortQuery }) => {
  const theme = useTheme();

  return (
    <Grid
      item
      sx={{
        minWidth: 0,
        width: 300,
        [theme.breakpoints.down('sm')]: {
          width: '100%',
        },
      }}
    >
      <SelectPaginate
        apiUrl={'api/representative-companies/by-manufacturer'}
        placeholder="Filtrar por representante"
        onChange={handleSearchRepresentative}
        value={searchRepresentative ? { id: 0, name: searchRepresentative } : null}
        valueName="name"
        label="Representante"
        isRequired
        icon="sort-down"
        statusFilter
        sortQuery={sortQuery}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            event.preventDefault();
            getAllEntities();
          }
        }}
      />
    </Grid>
  );
};

const LicensePlateFilter = ({
  searchLicensePlate,
  searchRepresentative,
  searchCompanyName,
  handleSearchLicensePlate,
  setActivePage,
  getAllEntities,
  loading,
}) => {
  const theme = useTheme();

  return (
    <Grid
      item
      display="flex"
      sx={{
        [theme.breakpoints.down('sm')]: {
          width: '100%',
        },
      }}
    >
      <Box
        sx={{
          [theme.breakpoints.down('sm')]: {
            width: '100%',
          },
          [theme.breakpoints.up('sm')]: {
            maxWidth: '250px',
          },
          marginTop: '5px',
        }}
      >
        <FormControl fullWidth>
          <Typography textAlign="left" color="text.primary" fontWeight="600" fontSize="16px">
            Placa
          </Typography>
          <OutlinedInput
            name="filterByName"
            placeholder="Filtrar por placa"
            value={searchLicensePlate}
            disabled={loading}
            onChange={handleSearchLicensePlate}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                setActivePage();
                getAllEntities();
              }
            }}
            sx={{
              width: '100%',
              height: '43px',
              borderRadius: '8px',
              fontSize: '.875rem',
              '& .MuiOutlinedInput-input': {
                padding: '14px 6px 14px 9.5px',
              },
              '& fieldset': {
                top: '0',
                borderWidth: '1px',
                borderColor: '#808080!important',
              },
            }}
          />
        </FormControl>
      </Box>
      <Box sx={{ marginLeft: 1, marginTop: '5px', alignSelf: 'flex-end' }}>
        <Button
          color="secondary"
          variant="contained"
          onClick={() => [setActivePage(), getAllEntities()]}
          disabled={!searchLicensePlate && !searchRepresentative && !searchCompanyName}
          sx={{
            borderRadius: 2,
            padding: '15px 0px',
          }}
        >
          <FontAwesomeIcon icon={loading ? 'sync' : 'search'} spin={loading} />
        </Button>
      </Box>
    </Grid>
  );
};

const LicensePlateSelectFilter = ({
  value,
  onChange,
  apiUrl = 'api/truck-implements/license-plates',
  loading,
  disabled = false,
  clearable = false,
  filterWidthSm,
  filterWidthMd,
}) => {
  const theme = useTheme();

  return (
    <Grid
      item
      sx={{
        minWidth: 0,
        width: filterWidthMd || 300,
        [theme.breakpoints.down('sm')]: {
          width: '100%',
        },
      }}
    >
      <SelectPaginateFromPaginatedApi
        apiUrl={apiUrl}
        value={value}
        valueName="licensePlate"
        searchName="searchLicensePlate"
        placeholder="Buscar por placa"
        label="Placa"
        isRequired
        isClearable={clearable}
        isDisabled={loading || disabled}
        minWidth={filterWidthSm}
        desktopMinWidth={filterWidthMd}
        displayFormat={option => option?.licensePlate || String(option?.id || '')}
        onChange={onChange}
      />
    </Grid>
  );
};

const EventSelectFilter = ({ value, options, onChange, label = 'Evento', placeholder = 'Todos os eventos', loading, filterWidthSm, filterWidthMd }) => {
  const theme = useTheme();

  return (
    <Grid
      item
      sx={{
        minWidth: 0,
        width: filterWidthMd || 300,
        [theme.breakpoints.down('sm')]: {
          width: '100%',
        },
      }}
    >
      <SelectPaginate
        value={value}
        staticOptions={options}
        valueName="name"
        placeholder={placeholder}
        label={label}
        isRequired
        isClearable
        isDisabled={loading}
        debounceTimeout={300}
        minWidth={filterWidthSm}
        desktopMinWidth={filterWidthMd}
        onChange={onChange}
      />
    </Grid>
  );
};

const SearchButton = ({ loading, getAllEntities, setActivePage, disabled = false }) => (
  <Grid item>
    <Button
      color="secondary"
      variant="contained"
      disabled={loading || disabled}
      onClick={() => {
        setActivePage?.();
        getAllEntities?.();
      }}
      sx={{
        borderRadius: 2,
        padding: '15px 0px',
      }}
    >
      <FontAwesomeIcon icon={loading ? 'sync' : 'search'} spin={loading} />
    </Button>
  </Grid>
);

const FilterOrder = ({ sortByName, sort, paginationState, isAdmin, isClassification }) => {
  const theme = useTheme();

  return (
    <Grid
      item
      display="flex"
      sx={{
        [theme.breakpoints.down('sm')]: {
          width: '100%',
        },
      }}
    >
      <Box
        sx={{
          [theme.breakpoints.down('sm')]: {
            width: '100%',
          },
          [theme.breakpoints.up('sm')]: {
            maxWidth: '250px',
          },
          marginTop: '5px',
        }}
      >
        <Typography textAlign="left" color="text.primary" fontWeight="600" fontSize="16px">
          Ordenar por
        </Typography>
        <SelectDefault value={paginationState.sort} onChange={event => sortByName(event.target.value)}>
          {isClassification && <MenuItem value="score">{translate('fleetSenseApp.ControlTower.Home.score')}</MenuItem>}
          {!isClassification && (
            <>
              <MenuItem value="licensePlate">{translate('fleetSenseApp.truckImplement.licensePlate')}</MenuItem>
              <MenuItem value="chassisNumber">{translate('fleetSenseApp.truckImplement.chassisNumber')}</MenuItem>
              {isAdmin && <MenuItem value="company.name">{translate('fleetSenseApp.truckImplement.company')}</MenuItem>}
            </>
          )}
        </SelectDefault>
      </Box>
      <Box sx={{ marginLeft: '8px', marginTop: '5px', alignSelf: 'flex-end' }}>
        <Button
          color="secondary"
          variant="contained"
          onClick={sort}
          sx={{
            borderRadius: '8px',
            padding: { xs: '10px 0px' },
          }}
        >
          <FontAwesomeIcon icon={paginationState.order === ASC ? 'sort-up' : 'sort-down'} />
          <Typography ml={'2px'}>{paginationState.order === ASC ? ' A-Z' : ' Z-A'}</Typography>
        </Button>
      </Box>
    </Grid>
  );
};

const ClearFilters = ({
  searchCompanyName,
  searchLicensePlate,
  searchRepresentative,
  searchCluster,
  status,
  sortClear,
  loading,
  hasExtraFilters,
  compact = false,
}) => {
  return (
    (searchCompanyName || searchLicensePlate || searchRepresentative || searchCluster || status !== StatusFilter.ACTIVATED || hasExtraFilters) && (
      <Grid item sx={{ marginLeft: compact ? '-8px' : undefined }}>
        <Button
          color="warning"
          variant="contained"
          aria-label="Limpar Filtros"
          title="Limpar Filtros"
          disabled={loading}
          onClick={() => sortClear()}
          sx={{
            borderRadius: '8px',
            padding: '15px 0px',
          }}
        >
          <Box display={'flex'} alignItems={'center'} alignSelf={'flex-end'}>
            <FontAwesomeIcon icon={loading ? 'sync' : 'times'} spin={loading} />
          </Box>
        </Button>
      </Grid>
    )
  );
};

const PeriodFilters = ({
  periodicType,
  sendSensorDataByImplementIdAndPeriodic,
  changeToCustomPeriodic,
  setLoading,
  sendSensorDataByImplement,
  setDateStart,
  dateStart,
  setDateEnd,
  dateEnd,
  isOpen,
  toggle,
  isCompany,
  onlyPastData,
  noTime,
  isPeriodAbsolute = false,
  showMonthlyPeriods = false,
  periodSelect = false,
  monthOnly = false,
  submitLabel,
  appliedPeriodLabel,
  maxRangeDays,
  maxRangeDisplayDays,
  loading = false,
}) => {
  const theme = useTheme();

  const absoluteStyle = {
    position: { xs: 'unset', lg: 'absolute' },
    left: 0,
    right: 0,
    top: 30,
    width: 'min-content',
    margin: '0 auto',
  };

  return (
    <Grid
      item
      sx={{
        pointerEvents: loading ? 'none' : 'auto',
        opacity: loading ? 0.65 : 1,
        position: 'relative',
        ...(isPeriodAbsolute && absoluteStyle),
        [theme.breakpoints.down('sm')]: {
          width: '100%',
          textAlign: 'left',
        },
      }}
    >
      <Typography textAlign="left" color="text.primary" fontWeight="600" fontSize="16px" style={showElement(!isPeriodAbsolute)}>
        Período
      </Typography>
      {PeriodButtonsNew(
        periodicType,
        sendSensorDataByImplementIdAndPeriodic,
        changeToCustomPeriodic,
        isCompany,
        setLoading,
        onlyPastData,
        showMonthlyPeriods,
        periodSelect
      )}
      {appliedPeriodLabel && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          textAlign="left"
          sx={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, whiteSpace: 'nowrap' }}
        >
          Período aplicado: {appliedPeriodLabel}
        </Typography>
      )}
      {ValidatedFormsNew({
        sendSensorDataByImplement,
        setDateStart,
        dateStart,
        setDateEnd,
        dateEnd,
        isOpen,
        toggle,
        noTime,
        monthOnly,
        submitLabel,
        maxRangeDays,
        maxRangeDisplayDays,
      })}
    </Grid>
  );
};

const AlertType = ({ activeButton, handleActiveButton, ActiveButton, enabledAlertTypes, alertTypeSelect }) => {
  const theme = useTheme();

  return (
    <Grid
      item
      sx={{
        [theme.breakpoints.down('sm')]: {
          width: '100%',
        },
      }}
    >
      <Typography textAlign="left" color="text.primary" fontWeight="600" fontSize="16px">
        Tipo
      </Typography>
      {alertTypeSelect ? (
        <SelectDefault value={activeButton} onChange={event => handleActiveButton(event as any, event.target.value)} mobileFullWidth minWidth={125}>
          {Object.values<string>(ActiveButton).map(value => (
            <MenuItem key={value} value={value} disabled={enabledAlertTypes ? !enabledAlertTypes.includes(value) : false}>
              <Typography sx={{ fontSize: '.875rem' }}>
                {value === ActiveButton.SENSORS
                  ? 'Sensores'
                  : value === ActiveButton.FENCES
                  ? 'Cercas'
                  : value === ActiveButton.IDENTIFICATION_CODE_ALERT
                  ? 'Identificação'
                  : value === ActiveButton.ROUTES
                  ? 'Rotas'
                  : value === ActiveButton.CAMERAS
                  ? 'Câmeras'
                  : ''}
              </Typography>
            </MenuItem>
          ))}
        </SelectDefault>
      ) : (
        <ToggleButtonGroup
          color="secondary"
          size="medium"
          value={activeButton}
          exclusive
          onChange={handleActiveButton}
          sx={{
            width: '100%',
            ...(ActiveButton.CAMERAS && {
              display: { xs: 'grid', sm: 'flex' },
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            }),
          }}
        >
          {Object.values<string>(ActiveButton).map((value, index) => (
            <ToggleButton
              key={index}
              value={value}
              disabled={enabledAlertTypes ? !enabledAlertTypes.includes(value) : false}
              sx={{
                width: '100%',
                textTransform: 'none',
                borderColor: 'primary.light',
                padding: '10.2px 10px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '.875rem',
                }}
              >
                {value === ActiveButton.SENSORS
                  ? 'Sensores'
                  : value === ActiveButton.FENCES
                  ? 'Cercas'
                  : value === ActiveButton.IDENTIFICATION_CODE_ALERT
                  ? 'Identificação'
                  : value === ActiveButton.ROUTES
                  ? 'Rotas'
                  : value === ActiveButton.CAMERAS
                  ? 'Câmeras'
                  : ''}
              </Typography>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
    </Grid>
  );
};

const Filters = ({ searchTypes, setActivePage }) => {
  const theme = useTheme();

  return (
    <Fragment>
      {searchTypes.map((item: ISearchTypes, key) => {
        return item.isVisible || item.isVisible === undefined ? (
          <Grid
            item
            key={key}
            sx={{
              [theme.breakpoints.down('sm')]: {
                width: '100%',
              },
            }}
          >
            <NewCustomSearchInput
              label={item.label}
              placeholder={item.placeholder ?? ''}
              setActivePage={setActivePage}
              nameSearch={item.value ?? ''}
              setNameSearch={item.setValue ?? (() => undefined)}
            />
          </Grid>
        ) : null;
      })}
    </Fragment>
  );
};

const Status = ({ setStatus, status }) => {
  const theme = useTheme();

  return (
    <Grid
      item
      sx={{
        [theme.breakpoints.down('sm')]: {
          width: '100%',
          textAlign: 'left',
        },
      }}
    >
      <StatusFilterSelect setStatus={setStatus} status={status} />
    </Grid>
  );
};

const EnumSelectCluster = ({ setSearchCluster, searchCluster, loading, setActivePage, getAllEntities }) => {
  const theme = useTheme();

  return (
    <Grid
      item
      sx={{
        display: 'flex',
        [theme.breakpoints.down('sm')]: {
          width: '100%',
          textAlign: 'left',
        },
      }}
    >
      <EnumSelectAlertCluster setCluster={setSearchCluster} cluster={searchCluster} />
      <Box sx={{ marginLeft: 1, marginTop: '5px', alignSelf: 'flex-end' }}>
        <Button
          color="secondary"
          variant="contained"
          onClick={() => [setActivePage(), getAllEntities()]}
          sx={{
            borderRadius: 2,
            padding: '15px 0px',
          }}
        >
          <FontAwesomeIcon icon={loading ? 'sync' : 'search'} spin={loading} />
        </Button>
      </Box>
    </Grid>
  );
};

export const PageFilters: React.FC<PageFiltersProps> = ({
  sortQuery,
  setActivePage,
  isAdmin = false,
  isManufacturerAdmin = false,
  searchTypes,
  status,
  setStatus,
  setTireAlertSearch,
  typeTireAlert,
  showFilters = [],
  searchCompanyName,
  getAllEntities,
  handleSearchCompany,
  companySelectApiUrl,
  companySelectClearable,
  searchLicensePlate,
  handleSearchLicensePlate,
  licensePlateSelectValue,
  handleSearchLicensePlateSelect,
  licensePlateSelectApiUrl,
  licensePlateSelectDisabled,
  licensePlateSelectClearable,
  eventSelectValue,
  eventSelectOptions,
  handleEventSelect,
  eventSelectLabel,
  eventSelectPlaceholder,
  paginationState,
  sort,
  sortByName,
  sortClear,
  loading,
  sendSensorDataByImplement,
  setDateStart,
  dateStart,
  setDateEnd,
  dateEnd,
  periodicType,
  sendSensorDataByImplementIdAndPeriodic,
  changeToCustomPeriodic,
  isOpen,
  toggle,
  isCompany,
  setLoading,
  activeButton,
  handleActiveButton,
  ActiveButton,
  enabledAlertTypes,
  alertTypeSelect,
  periodSelect,
  isPeriodAbsolute,
  searchRepresentative,
  handleSearchRepresentative,
  dateSearch,
  handleDateSearch,
  searchCluster,
  setSearchCluster,
  onlyPastData,
  noTime,
  isClassification,
  showMonthlyPeriods,
  monthOnly,
  submitLabel,
  appliedPeriodLabel,
  maxRangeDays,
  maxRangeDisplayDays,
  onlyTransporters = false,
  childrenAfterLicensePlate = false,
  compactClearFilters = false,
  searchButtonDisabled = false,
  filterWidthSm,
  filterWidthMd,
  compact = false,
  children,
  hasExtraFilters = false,
}) => {
  const theme = useTheme();
  return (
    <Grid
      container
      gap={2}
      sx={{
        justifyContent: compact ? 'flex-start' : 'center',
        alignItems: 'flex-end',
        marginBottom: compact ? 0 : 1,
        paddingBottom: appliedPeriodLabel ? '20px' : 0,
        display: 'flex',
      }}
    >
      {showFilters.includes('status') && <Status setStatus={setStatus} status={status} />}

      {showFilters.includes('periodic') && (
        <PeriodFilters
          periodicType={periodicType}
          sendSensorDataByImplementIdAndPeriodic={sendSensorDataByImplementIdAndPeriodic}
          changeToCustomPeriodic={changeToCustomPeriodic}
          setLoading={setLoading}
          sendSensorDataByImplement={sendSensorDataByImplement}
          setDateStart={setDateStart}
          dateStart={dateStart}
          setDateEnd={setDateEnd}
          dateEnd={dateEnd}
          isOpen={isOpen}
          toggle={toggle}
          isCompany={isCompany}
          isPeriodAbsolute={isPeriodAbsolute}
          onlyPastData={onlyPastData}
          noTime={noTime}
          showMonthlyPeriods={showMonthlyPeriods}
          periodSelect={periodSelect}
          monthOnly={monthOnly}
          submitLabel={submitLabel}
          appliedPeriodLabel={appliedPeriodLabel}
          maxRangeDays={maxRangeDays}
          maxRangeDisplayDays={maxRangeDisplayDays}
          loading={loading}
        />
      )}

      {showFilters.includes('alert') && (
        <AlertType
          activeButton={activeButton}
          handleActiveButton={handleActiveButton}
          ActiveButton={ActiveButton}
          enabledAlertTypes={enabledAlertTypes}
          alertTypeSelect={alertTypeSelect}
        />
      )}

      {isManufacturerAdmin && showFilters.includes('manufacturer') && (
        <ManufacturerFilter
          sortQuery={sortQuery}
          handleSearchRepresentative={handleSearchRepresentative}
          searchRepresentative={searchRepresentative}
          getAllEntities={getAllEntities}
        />
      )}

      {showFilters.includes('date') && <DateInput onChange={handleDateSearch ?? (() => undefined)} dateSearch={dateSearch ?? ''} />}

      {isAdmin && showFilters.includes('company') && (
        <CompanyFilter
          sortQuery={sortQuery}
          handleSearchCompany={handleSearchCompany}
          searchRepresentative={searchRepresentative}
          searchCompanyName={searchCompanyName}
          getAllEntities={getAllEntities}
          companySelectApiUrl={companySelectApiUrl}
          companySelectClearable={companySelectClearable}
          onlyTransporters={onlyTransporters}
          loading={loading}
          filterWidthSm={filterWidthSm}
          filterWidthMd={filterWidthMd}
        />
      )}

      {!childrenAfterLicensePlate && children}

      {showFilters.includes('licensePlate') && (
        <LicensePlateFilter
          searchLicensePlate={searchLicensePlate}
          searchRepresentative={searchRepresentative}
          searchCompanyName={searchCompanyName}
          handleSearchLicensePlate={handleSearchLicensePlate}
          getAllEntities={getAllEntities}
          setActivePage={setActivePage}
          loading={loading}
        />
      )}

      {showFilters.includes('licensePlateSelect') && (
        <LicensePlateSelectFilter
          value={licensePlateSelectValue}
          onChange={handleSearchLicensePlateSelect}
          apiUrl={licensePlateSelectApiUrl}
          loading={loading}
          disabled={licensePlateSelectDisabled}
          clearable={licensePlateSelectClearable}
          filterWidthSm={filterWidthSm}
          filterWidthMd={filterWidthMd}
        />
      )}

      {showFilters.includes('eventSelect') && (
        <EventSelectFilter
          value={eventSelectValue}
          options={eventSelectOptions}
          onChange={handleEventSelect}
          label={eventSelectLabel}
          placeholder={eventSelectPlaceholder}
          loading={loading}
          filterWidthSm={filterWidthSm}
          filterWidthMd={filterWidthMd}
        />
      )}

      {showFilters.includes('filters') && searchTypes && <Filters searchTypes={searchTypes} setActivePage={setActivePage} />}

      {childrenAfterLicensePlate && children}

      {showFilters.includes('searchButton') && (
        <SearchButton loading={loading} getAllEntities={getAllEntities} setActivePage={setActivePage} disabled={searchButtonDisabled} />
      )}

      {showFilters.includes('filterOrder') && (
        <FilterOrder sortByName={sortByName} sort={sort} paginationState={paginationState} isAdmin={isAdmin} isClassification={isClassification} />
      )}

      {showFilters.includes('tireAlert') && (
        <EnumSelectTireAlert setAlertSearch={setTireAlertSearch} setActivePage={setActivePage} typeTireAlert={typeTireAlert} />
      )}
      {showFilters.includes('alertCluster') && (
        <EnumSelectCluster
          searchCluster={searchCluster}
          setSearchCluster={setSearchCluster}
          loading={loading}
          setActivePage={setActivePage}
          getAllEntities={getAllEntities}
        />
      )}
      {showFilters.includes('clearFilters') && (
        <ClearFilters
          searchCompanyName={searchCompanyName}
          searchLicensePlate={searchLicensePlate}
          searchCluster={searchCluster}
          searchRepresentative={searchRepresentative}
          status={status}
          sortClear={sortClear}
          loading={loading}
          hasExtraFilters={hasExtraFilters}
          compact={compactClearFilters}
        />
      )}
    </Grid>
  );
};
